/**
 * ISO-3166-1 alpha-2 country list for OKS-22 signup form.
 *
 * Per UX Spec §2 + BE TRD §"Data Model Delta" (`country` columns CHECK
 * `country IN (alpha-2 codes)`), the wire-format value MUST be a
 * 2-letter ISO code (e.g. `"FR"`, `"US"`). The display label is
 * locale-aware via `Intl.DisplayNames` so we don't have to ship +250 ×
 * 2-locale name translations by hand.
 *
 * **List scope (v1):** The full ISO list (250+ codes) is overkill for
 * Paalm's launch cohort. Ship a curated subset that covers the active
 * Paalm market + every G20 + EU member + Crew's onboarded brands'
 * known territories. Add codes here when a new market opens —
 * additions are zero-risk (new option, no migration).
 *
 * Ordered alphabetically by code; the `<Select>` consumer sorts by
 * locale-aware display label at render time so users see a familiar
 * order ("France" / "Allemagne" sort correctly per FR locale).
 */
export interface CountryOption {
  /** ISO-3166-1 alpha-2 (uppercase). Wire value. */
  code: string
}

export const COUNTRIES: readonly CountryOption[] = [
  { code: "AE" }, // United Arab Emirates
  { code: "AR" }, // Argentina
  { code: "AT" }, // Austria
  { code: "AU" }, // Australia
  { code: "BE" }, // Belgium
  { code: "BR" }, // Brazil
  { code: "CA" }, // Canada
  { code: "CH" }, // Switzerland
  { code: "CL" }, // Chile
  { code: "CN" }, // China
  { code: "CO" }, // Colombia
  { code: "CZ" }, // Czechia
  { code: "DE" }, // Germany
  { code: "DK" }, // Denmark
  { code: "EE" }, // Estonia
  { code: "ES" }, // Spain
  { code: "FI" }, // Finland
  { code: "FR" }, // France
  { code: "GB" }, // United Kingdom
  { code: "GR" }, // Greece
  { code: "HK" }, // Hong Kong
  { code: "HU" }, // Hungary
  { code: "ID" }, // Indonesia
  { code: "IE" }, // Ireland
  { code: "IL" }, // Israel
  { code: "IN" }, // India
  { code: "IT" }, // Italy
  { code: "JP" }, // Japan
  { code: "KR" }, // South Korea
  { code: "LU" }, // Luxembourg
  { code: "MA" }, // Morocco
  { code: "MX" }, // Mexico
  { code: "MY" }, // Malaysia
  { code: "NL" }, // Netherlands
  { code: "NO" }, // Norway
  { code: "NZ" }, // New Zealand
  { code: "PH" }, // Philippines
  { code: "PL" }, // Poland
  { code: "PT" }, // Portugal
  { code: "RO" }, // Romania
  { code: "SA" }, // Saudi Arabia
  { code: "SE" }, // Sweden
  { code: "SG" }, // Singapore
  { code: "SK" }, // Slovakia
  { code: "TH" }, // Thailand
  { code: "TR" }, // Türkiye
  { code: "TW" }, // Taiwan
  { code: "UA" }, // Ukraine
  { code: "US" }, // United States
  { code: "VN" }, // Vietnam
  { code: "ZA" }, // South Africa
] as const

/**
 * Resolve a country code to its locale-aware display label via the
 * Intl API. Falls back to the code itself if the locale can't render
 * it (older Node test environments occasionally lack the data).
 *
 * Pure function — easy to unit-test without React.
 */
export function countryLabel(code: string, locale: "en" | "fr"): string {
  try {
    const dn = new Intl.DisplayNames([locale], { type: "region" })
    return dn.of(code) ?? code
  } catch {
    return code
  }
}

/**
 * Sort a country list by locale-aware display label. Returns a NEW
 * array — does not mutate the input. Memoise at the call site if the
 * list is rendered frequently; for OKS-22 it sorts once per page mount,
 * which is fine.
 */
export function sortCountriesByLabel(
  countries: readonly CountryOption[],
  locale: "en" | "fr",
): CountryOption[] {
  return [...countries].sort((a, b) =>
    countryLabel(a.code, locale).localeCompare(
      countryLabel(b.code, locale),
      locale,
    ),
  )
}

/**
 * Strict ISO-2 validator. Used by Vitest canaries to assert FE submits
 * the right wire value (NOT a free-text country name). Mirrors the BE
 * `country ~ /\A[A-Z]{2}\z/` CHECK constraint.
 */
export const ISO_3166_ALPHA_2_PATTERN = /^[A-Z]{2}$/
